import { INCOME_CATEGORIES } from "@gladis/shared";
import { Button, Dialog, Label, MoneyInput, SelectField, TextField } from "@gladis/ui";
import { incomeCategoryLabel, useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import type { IncomeSourceDto } from "../dto.js";
import { intervalOptions } from "../intervals.js";

/**
 * Add an income source, or edit one's identity.
 *
 * ==========================================================================================
 * EDITING DOES NOT OFFER THE AMOUNT OR THE INTERVAL
 * ==========================================================================================
 *
 * Both are facts about a DATE and live in the amount history, which has its own panel. Offering them
 * here would be offering a member a way to rewrite every earlier month without being told that is
 * what they were doing — the exact defect the module is built to prevent, wearing a form field.
 *
 * On CREATE both appear, because a source cannot exist without an opening amount: the two writes
 * happen in one transaction, and a source with no amount is a row the reconstruction skips entirely.
 */

export interface SourceFormProps {
  open: boolean;
  onClose: () => void;
  /** `null` to add. An existing source to edit its identity. */
  editing: IncomeSourceDto | null;
  members: readonly { userId: string; displayName: string }[];
  onCreate: (input: {
    name: string;
    category: string;
    ownership: string;
    ownerId: string | null;
    amount: string;
    interval: string;
    effectiveFrom: string;
  }) => void;
  onSave: (input: {
    name: string;
    category: string;
    ownership: string;
    ownerId: string | null;
  }) => void;
  pending: boolean;
  error?: string | null;
}

export function SourceForm({
  open,
  onClose,
  editing,
  members,
  onCreate,
  onSave,
  pending,
  error,
}: SourceFormProps): ReactNode {
  const t = useT();
  const [name, setName] = useState(editing?.name ?? "");
  const [category, setCategory] = useState(editing?.category ?? "salary");
  const [ownership, setOwnership] = useState<string>(editing?.ownership ?? "individual");
  const [ownerId, setOwnerId] = useState(editing?.ownerId ?? members[0]?.userId ?? "");
  const [amount, setAmount] = useState("");
  const [interval, setInterval] = useState("monthly");
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));

  function submit(event: FormEvent): void {
    event.preventDefault();
    const owner = ownership === "individual" ? ownerId || null : null;

    if (editing) onSave({ name, category, ownership, ownerId: owner });
    else onCreate({ name, category, ownership, ownerId: owner, amount, interval, effectiveFrom });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t("inc.source.editHeading") : t("inc.source.addHeading")}
      description={editing ? t("inc.source.datedFacts") : t("inc.source.hint")}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="income-name">{t("inc.source.name")}</Label>
          <TextField
            id="income-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("inc.source.namePlaceholder")}
            required
            maxLength={120}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="income-category">{t("inc.source.category")}</Label>
            <SelectField
              id="income-category"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              {INCOME_CATEGORIES.map((option) => (
                <option key={option.value} value={option.value}>
                  {incomeCategoryLabel(option.value)}
                </option>
              ))}
            </SelectField>
          </div>

          <div>
            <Label htmlFor="income-ownership">{t("inc.source.whose")}</Label>
            <SelectField
              id="income-ownership"
              value={ownership}
              onChange={(event) => setOwnership(event.target.value)}
            >
              <option value="individual">{t("inc.source.oneOfUs")}</option>
              <option value="joint">{t("inc.source.household")}</option>
            </SelectField>
          </div>
        </div>

        {ownership === "individual" ? (
          <div>
            <Label htmlFor="income-owner">{t("inc.source.earnedBy")}</Label>
            <SelectField
              id="income-owner"
              value={ownerId}
              onChange={(event) => setOwnerId(event.target.value)}
            >
              {members.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.displayName}
                </option>
              ))}
            </SelectField>
          </div>
        ) : null}

        {editing === null ? (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="income-amount">{t("inc.source.amount")}</Label>
              <MoneyInput id="income-amount" value={amount} onValueChange={setAmount} required />
            </div>
            <div>
              <Label htmlFor="income-interval">{t("inc.source.howOften")}</Label>
              <SelectField
                id="income-interval"
                value={interval}
                onChange={(event) => setInterval(event.target.value)}
              >
                {intervalOptions().map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </SelectField>
            </div>
            <div>
              <Label htmlFor="income-from">{t("inc.source.from")}</Label>
              <TextField
                id="income-from"
                type="date"
                value={effectiveFrom}
                onChange={(event) => setEffectiveFrom(event.target.value)}
                required
              />
              {/* Backdating is the supported way to enter years that predate GLADIS, so the hint
                  invites it rather than warning about it. */}
              <p className="text-xs text-muted mt-1">{t("inc.source.fromHint")}</p>
            </div>
          </div>
        ) : null}

        {error ? <p className="text-sm text-danger">{error}</p> : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={pending}>
            {editing ? t("common.save") : t("inc.source.add")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
